<template>
  <div class="main-navigation-wrapper">
    <!-- Desktop Navigation -->
    <nav class="main-navigation" role="navigation" aria-label="Main navigation">
      <div class="nav-brand">
        <router-link to="/" class="brand-link">
          <Music :size="24" class="brand-icon" />
          <span class="brand-text">Tierlist</span>
        </router-link>
      </div>
      
      <div class="nav-items">
        <router-link 
          v-for="item in navigationItems" 
          :key="item.name"
          :to="item.route"
          class="nav-item"
          :class="{ 'active': isActiveRoute(item.route) }"
          :aria-label="item.description"
        >
          <component :is="item.icon" :size="20" class="nav-icon" />
          <span class="nav-text">{{ item.name }}</span>
        </router-link>
        
        <!-- Desktop Admin Section -->
        <div class="admin-section desktop-admin">
          <button 
            class="admin-toggle nav-item" 
            :class="{ 'active': isAdminRouteActive() }"
            @click="toggleAdminSection"
            aria-label="Admin functions"
          >
            <Settings :size="20" class="nav-icon" />
            <span class="nav-text">Admin</span>
            <span class="admin-arrow" :class="{ 'open': adminSectionOpen }">▼</span>
          </button>
          
          <div v-if="adminSectionOpen" class="admin-dropdown">
            <router-link 
              v-for="item in adminItems" 
              :key="item.name"
              :to="item.route"
              class="admin-item"
              :class="{ 'active': isActiveRoute(item.route) }"
              :aria-label="item.description"
              @click="adminSectionOpen = false"
            >
              <component :is="item.icon" :size="20" class="nav-icon" />
              <span class="nav-text">{{ item.name }}</span>
            </router-link>
            
            <!-- Admin Authentication Status -->
            <div v-if="isAdmin" class="admin-action-item auth-status">
              <div class="auth-indicator">
                <Shield :size="16" class="auth-icon" />
                <span class="auth-text">Admin Authenticated</span>
              </div>
              <button class="auth-logout-btn" @click="handleLogout">
                Logout
              </button>
            </div>
            
            <!-- Test Data Visibility Toggle -->
            <div class="admin-action-item test-mode-toggle">
              <label class="toggle-label">
                <input 
                  type="checkbox" 
                  v-model="showTestAlbums"
                  class="toggle-checkbox"
                />
                <span class="toggle-text">Show Test Albums</span>
                <span class="toggle-help">
                  {{ showTestAlbums ? 'Test data visible' : 'Test data hidden' }}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Mobile Hamburger Toggle -->
    <div class="mobile-nav-toggle" :class="{ 'menu-open': mobileMenuOpen }" @click="toggleMobileMenu" :aria-expanded="mobileMenuOpen">
      <button 
        class="hamburger-btn"
        :class="{ 'open': mobileMenuOpen }"
        aria-label="Toggle navigation menu"
      >
        <Menu :size="24" />
      </button>
    </div>

    <!-- Mobile Navigation Menu -->
    <transition name="mobile-menu">
      <div 
        v-if="mobileMenuOpen" 
        class="mobile-nav-overlay"
        @click="closeMobileMenu"
      >
        <nav class="mobile-nav-menu" role="navigation" aria-label="Mobile navigation" @click.stop>
          <div class="mobile-nav-header">
            <div class="mobile-brand">
              <Music :size="24" class="brand-icon" />
              <span class="brand-text">Tierlist</span>
            </div>
            <button 
              class="close-btn"
              @click="closeMobileMenu"
              aria-label="Close navigation menu"
            >
              <X :size="24" />
            </button>
          </div>
          
          <div class="mobile-nav-items">
            <router-link 
              v-for="item in navigationItems" 
              :key="item.name"
              :to="item.route"
              class="mobile-nav-item"
              :class="{ 'active': isActiveRoute(item.route) }"
              @click="closeMobileMenu"
            >
              <component :is="item.icon" :size="20" class="nav-icon" />
              <div class="nav-content">
                <span class="nav-text">{{ item.name }}</span>
                <span class="nav-description">{{ item.description }}</span>
              </div>
            </router-link>
            
            <!-- Mobile Admin Section -->
            <div class="mobile-admin-section">
              <button 
                class="mobile-admin-toggle"
                :class="{ 'active': isAdminRouteActive(), 'open': adminSectionOpen }"
                @click="toggleAdminSection"
                aria-label="Admin functions"
              >
                <Settings :size="20" class="nav-icon" />
                <div class="nav-content">
                  <span class="nav-text">Admin</span>
                  <span class="nav-description">Administrative functions</span>
                </div>
                <span class="admin-arrow" :class="{ 'open': adminSectionOpen }">▼</span>
              </button>
              
              <div v-if="adminSectionOpen" class="mobile-admin-items">
                <router-link 
                  v-for="item in adminItems" 
                  :key="item.name"
                  :to="item.route"
                  class="mobile-admin-item"
                  :class="{ 'active': isActiveRoute(item.route) }"
                  @click="closeMobileMenu"
                >
                  <component :is="item.icon" :size="20" class="nav-icon" />
                  <div class="nav-content">
                    <span class="nav-text">{{ item.name }}</span>
                    <span class="nav-description">{{ item.description }}</span>
                  </div>
                </router-link>
                
                <!-- Admin Authentication Status - Mobile -->
                <div v-if="isAdmin" class="mobile-admin-action auth-status-mobile">
                  <div class="mobile-auth-indicator">
                    <Shield :size="16" class="auth-icon" />
                    <div class="nav-content">
                      <span class="nav-text">Admin Authenticated</span>
                      <span class="nav-description">Admin access active</span>
                    </div>
                  </div>
                  <button class="mobile-auth-logout-btn" @click="handleLogout">
                    Logout
                  </button>
                </div>
                
                <!-- Test Data Visibility Toggle - Mobile -->
                <div class="mobile-admin-action test-mode-toggle-mobile">
                  <label class="mobile-toggle-label">
                    <input 
                      type="checkbox" 
                      v-model="showTestAlbums"
                      class="mobile-toggle-checkbox"
                    />
                    <div class="nav-content">
                      <span class="nav-text">Show Test Albums</span>
                      <span class="nav-description">
                        {{ showTestAlbums ? 'Test data visible' : 'Test data hidden' }}
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { Home, BarChart3, FileText, Settings, Plus, Menu, X, Music, Shield, TrendingUp, Clock, Sparkles } from 'lucide-vue-next';
import { useTestMode } from '../composables/useTestMode.js';
import { useAdminCheck } from '../composables/useAdminAuth.js';
import { useAuthStore } from '../stores/auth.js';

export default {
  name: 'MainNavigation',
  components: {
    Home,
    BarChart3,
    FileText,
    Settings,
    Plus,
    Menu,
    X,
    Music,
    Shield,
    TrendingUp,
    Clock,
    Sparkles
  },
  setup() {
    const route = useRoute();
    const mobileMenuOpen = ref(false);
    const adminSectionOpen = ref(false);

    // Test mode functionality
    const { showTestAlbums } = useTestMode();

    // Admin authentication check
    const { isAdmin } = useAdminCheck();
    const authStore = useAuthStore();

    const navigationItems = computed(() => {
      return [
        {
          name: 'Sessions',
          route: '/',
          icon: 'Home',
          description: 'View all listening sessions'
        },
        {
          name: 'Tierlist',
          route: '/tierlist',
          icon: 'BarChart3',
          description: 'Browse albums by tier ranking'
        },
        {
          name: 'Changelog',
          route: '/changelog',
          icon: 'FileText',
          description: 'View rating change history'
        },
        {
          name: 'Stats',
          route: '/stats',
          icon: 'TrendingUp',
          description: 'View album ratings statistics'
        },
        {
          name: 'Timeline',
          route: '/timeline',
          icon: 'Clock',
          description: 'Browse albums by release year'
        },
        {
          name: 'v2 beta',
          route: '/v2',
          icon: 'Sparkles',
          description: 'Try the new book club app'
        }
      ];
    });

    const adminItems = computed(() => {
      const items = [
        {
          name: 'Sessions Admin',
          route: '/manage-sessions',
          icon: 'Settings',
          description: 'Create and manage sessions',
          requiresAuth: false
        },
        {
          name: 'Add Album',
          route: '/add-album',
          icon: 'Plus',
          description: 'Add new album to collection',
          requiresAuth: false
        }
      ];

      return items.filter(item => !item.requiresAuth || isAdmin.value);
    });

    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value;
    };

    const closeMobileMenu = () => {
      mobileMenuOpen.value = false;
    };

    const toggleAdminSection = () => {
      adminSectionOpen.value = !adminSectionOpen.value;
    };

    const isActiveRoute = (routePath) => {
      return route.path === routePath;
    };

    const isAdminRouteActive = () => {
      return adminItems.value.some(item => route.path === item.route);
    };

    const handleLogout = () => {
      authStore.logout();
      adminSectionOpen.value = false; // Close admin section after logout
      mobileMenuOpen.value = false; // Close mobile menu after logout
    };

    return {
      navigationItems,
      adminItems,
      mobileMenuOpen,
      adminSectionOpen,
      showTestAlbums,
      isAdmin,
      toggleMobileMenu,
      closeMobileMenu,
      toggleAdminSection,
      isActiveRoute,
      isAdminRouteActive,
      handleLogout
    };
  }
};
</script>

<style scoped>
.main-navigation-wrapper {
  position: relative;
}

/* Desktop Navigation */
.main-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 2rem;
  background: rgba(41, 41, 41, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-brand {
  display: flex;
  align-items: center;
}

.brand-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: #ffffff;
  font-weight: 700;
  font-size: 1.25rem;
}

.brand-icon {
  font-size: 1.5rem;
}

.brand-text {
  color: #ffffff;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 800;
  font-size: 1.5rem;
  letter-spacing: -0.025em;
}

.nav-items {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  color: #f3f3f3;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.nav-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 188, 212, 0.1), transparent);
  transition: left 0.5s;
}

.nav-item:hover::before {
  left: 100%;
}

.nav-item:hover {
  background: rgba(0, 188, 212, 0.1);
  color: #00bcd4;
  transform: translateY(-2px);
}

.nav-item.active {
  background: rgba(0, 188, 212, 0.2);
  color: #00bcd4;
}

.nav-icon {
  font-size: 1.1rem;
}

.nav-text {
  font-size: 0.95rem;
}

/* Desktop Admin Section */
.admin-section {
  position: relative;
}

.admin-toggle {
  background: none !important;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: inherit;
}

.admin-arrow {
  font-size: 0.75rem;
  margin-left: 0.25rem;
  transition: transform 0.2s ease;
}

.admin-arrow.open {
  transform: rotate(180deg);
}

.admin-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: rgba(41, 41, 41, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.5rem 0;
  min-width: 200px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  margin-top: 0.5rem;
}

.admin-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: rgba(243, 243, 243, 0.8);
  font-weight: 400;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

.admin-item:hover {
  background: rgba(0, 188, 212, 0.1);
  color: #00bcd4;
  border-left-color: rgba(0, 188, 212, 0.3);
}

.admin-item.active {
  background: rgba(0, 188, 212, 0.15);
  color: #00bcd4;
  border-left-color: #00bcd4;
}

.admin-item .nav-icon {
  font-size: 1rem;
}

.admin-item .nav-text {
  font-size: 0.875rem;
}

/* Admin Action Items - Desktop */
.admin-action-item {
  padding: 0.75rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.auth-status {
  background-color: rgba(0, 188, 212, 0.1);
  border-left: 3px solid rgba(0, 188, 212, 0.5);
}

.auth-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.auth-icon {
  color: #00bcd4;
}

.auth-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #00bcd4;
}

.auth-logout-btn {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 4px;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.auth-logout-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.4);
}

.test-mode-toggle {
  background-color: rgba(0, 0, 0, 0.2);
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  flex-wrap: wrap;
  width: 100%;
}

.toggle-checkbox {
  transform: scale(1.1);
  accent-color: #00bcd4;
}

.toggle-text {
  font-weight: 500;
  color: rgba(243, 243, 243, 0.9);
  font-size: 0.875rem;
}

.toggle-help {
  width: 100%;
  font-size: 0.75rem;
  color: rgba(243, 243, 243, 0.6);
  margin-top: 0.25rem;
  font-style: italic;
}

/* Mobile Navigation Toggle */
.mobile-nav-toggle {
  display: none;
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 200;
}

.mobile-nav-toggle.menu-open {
  z-index: 140; /* Lower than overlay when menu is open */
}

.hamburger-btn {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  background: rgba(41, 41, 41, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  padding: 0;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  color: #ffffff;
}

.hamburger-btn:hover {
  background: rgba(0, 188, 212, 0.1);
  border-color: rgba(0, 188, 212, 0.3);
}

.hamburger-line {
  width: 24px;
  height: 2px;
  background: #f3f3f3;
  margin: 2px 0;
  transition: all 0.3s ease;
  transform-origin: center;
}

.hamburger-btn.open .hamburger-line:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
}

.hamburger-btn.open .hamburger-line:nth-child(2) {
  opacity: 0;
}

.hamburger-btn.open .hamburger-line:nth-child(3) {
  transform: rotate(-45deg) translate(7px, -6px);
}

/* Mobile Navigation Menu */
.mobile-nav-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  z-index: 150;
  display: flex;
  justify-content: flex-end;
}

.mobile-nav-menu {
  width: 85%;
  max-width: 320px;
  height: 100vh;
  background: rgba(41, 41, 41, 0.98);
  backdrop-filter: blur(20px);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.mobile-nav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ffffff;
  font-weight: 700;
  font-size: 1.25rem;
}

.mobile-brand .brand-text {
  color: #ffffff;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 800;
  font-size: 1.5rem;
  letter-spacing: -0.025em;
}

.close-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: #f3f3f3;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;
}

.close-btn svg {
  width: 20px;
  height: 20px;
  color: #f3f3f3;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.close-btn:hover svg {
  color: #00bcd4;
}

.mobile-nav-items {
  flex: 1;
  padding: 1rem 0;
}

.mobile-nav-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  text-decoration: none;
  color: #f3f3f3;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
}

.mobile-nav-item:hover {
  background: rgba(0, 188, 212, 0.1);
  border-left-color: rgba(0, 188, 212, 0.3);
}

.mobile-nav-item.active {
  background: rgba(0, 188, 212, 0.15);
  border-left-color: #00bcd4;
  color: #00bcd4;
}

.mobile-nav-item .nav-icon {
  font-size: 1.25rem;
  min-width: 24px;
  text-align: center;
}

.nav-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.mobile-nav-item .nav-text {
  font-weight: 600;
  font-size: 1rem;
}

.nav-description {
  font-size: 0.875rem;
  color: rgba(243, 243, 243, 0.7);
}

.mobile-nav-item.active .nav-description {
  color: rgba(0, 188, 212, 0.8);
}

/* Mobile Admin Section */
.mobile-admin-section {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 1rem;
  padding-top: 1rem;
}

.mobile-admin-toggle {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  color: rgba(243, 243, 243, 0.8);
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
  width: 100%;
  text-align: left;
  position: relative;
}

.mobile-admin-toggle:hover {
  background: rgba(0, 188, 212, 0.05);
  border-left-color: rgba(0, 188, 212, 0.2);
}

.mobile-admin-toggle.active {
  background: rgba(0, 188, 212, 0.1);
  border-left-color: rgba(0, 188, 212, 0.5);
  color: #00bcd4;
}

.mobile-admin-toggle.open {
  background: rgba(0, 188, 212, 0.08);
}

.mobile-admin-toggle .admin-arrow {
  position: absolute;
  right: 1.5rem;
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}

.mobile-admin-toggle .admin-arrow.open {
  transform: rotate(180deg);
}

.mobile-admin-items {
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.mobile-admin-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 2rem;
  text-decoration: none;
  color: rgba(243, 243, 243, 0.7);
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
  font-size: 0.9rem;
}

.mobile-admin-item:hover {
  background: rgba(0, 188, 212, 0.08);
  border-left-color: rgba(0, 188, 212, 0.3);
  color: rgba(243, 243, 243, 0.9);
}

.mobile-admin-item.active {
  background: rgba(0, 188, 212, 0.12);
  border-left-color: #00bcd4;
  color: #00bcd4;
}

.mobile-admin-item .nav-icon {
  font-size: 1rem;
  min-width: 20px;
  text-align: center;
}

.mobile-admin-item .nav-text {
  font-weight: 500;
  font-size: 0.875rem;
}

.mobile-admin-item .nav-description {
  font-size: 0.8rem;
  color: rgba(243, 243, 243, 0.6);
}

.mobile-admin-item.active .nav-description {
  color: rgba(0, 188, 212, 0.7);
}

/* Admin Action Items - Mobile */
.mobile-admin-action {
  padding: 0.875rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.auth-status-mobile {
  background-color: rgba(0, 188, 212, 0.1);
  border-left: 4px solid rgba(0, 188, 212, 0.5);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.mobile-auth-indicator {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.mobile-auth-indicator .auth-icon {
  color: #00bcd4;
  min-width: 16px;
}

.mobile-auth-indicator .nav-text {
  font-weight: 500;
  color: #00bcd4;
}

.mobile-auth-indicator .nav-description {
  color: rgba(0, 188, 212, 0.8);
}

.mobile-auth-logout-btn {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  align-self: flex-start;
}

.mobile-auth-logout-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.4);
}

.test-mode-toggle-mobile {
  background-color: rgba(0, 0, 0, 0.2);
}

.mobile-toggle-label {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  width: 100%;
}

.mobile-toggle-checkbox {
  transform: scale(1.2);
  accent-color: #00bcd4;
  min-width: 20px;
}

/* Mobile Menu Animations */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
}

.mobile-menu-enter-from .mobile-nav-menu,
.mobile-menu-leave-to .mobile-nav-menu {
  transform: translateX(100%);
}

.mobile-menu-enter-active .mobile-nav-menu,
.mobile-menu-leave-active .mobile-nav-menu {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Responsive Behavior */
@media (max-width: 768px) {
  .main-navigation {
    display: none;
  }
  
  .mobile-nav-toggle {
    display: block;
  }
}

@media (max-width: 600px) {
  .mobile-nav-menu {
    width: 90%;
  }
  
  .mobile-nav-toggle {
    top: 0.75rem;
    right: 0.75rem;
  }
}

/* Focus States for Accessibility */
.nav-item:focus-visible,
.mobile-nav-item:focus-visible,
.hamburger-btn:focus-visible {
  outline: 3px solid rgba(0, 188, 212, 0.6);
  outline-offset: 2px;
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  .nav-item,
  .mobile-nav-item,
  .hamburger-btn,
  .hamburger-line,
  .mobile-menu-enter-active,
  .mobile-menu-leave-active {
    transition: none;
  }
  
  .nav-item:hover {
    transform: none;
  }
}
</style>